import { memo, useId, type MouseEvent as RME } from 'react';
import { MM_PER_PT, toCss, type ElementBox, type PageLayout, type Prim, type Rect } from '@cal/engine';
import { browserFonts } from '../fonts';
import { previewUrl, thumbUrl } from '../assets';

export interface Guides { bleed: boolean; safe: boolean; binding: boolean }
export type Handle = 'nw' | 'n' | 'ne' | 'e' | 'se' | 's' | 'sw' | 'w';
const HANDLES: Handle[] = ['nw', 'n', 'ne', 'e', 'se', 's', 'sw', 'w'];
export interface Geometry { safe: Rect; bindings: (Rect & { label?: string })[] }

interface Props {
  layout: PageLayout;
  geometry: Geometry;
  widthPx: number;
  quality: 'preview' | 'thumb';
  guides?: Guides;
  /** show the bleed area around the trim */
  showBleed?: boolean;
  selectedId?: string | null;
  overridden?: Set<string>;
  onSelect?: (id: string | null, e: RME) => void;
  onBoxPointerDown?: (box: ElementBox, e: React.PointerEvent) => void;
  /** resize handle pressed on the selected box: n/s/e/w and corners */
  onHandlePointerDown?: (box: ElementBox, handle: Handle, e: React.PointerEvent) => void;
  /** layout grid cells to show (custom builder) */
  gridCells?: Rect[];
  fontVersion: number;
  ariaLabel: string;
}

/**
 * Renders a page layout as SVG in millimetre user units. Text is drawn from
 * the same font glyph outlines and positions that the PDF export uses.
 */
function PageSvgImpl({ layout, geometry, widthPx, quality, guides, showBleed = true, selectedId, overridden, onSelect, onBoxPointerDown, onHandlePointerDown, gridCells, ariaLabel }: Props) {
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '');
  const b = showBleed ? layout.bleed : 0;
  const vb = `${-b} ${-b} ${layout.trimW + 2 * b} ${layout.trimH + 2 * b}`;
  const heightPx = (widthPx * (layout.trimH + 2 * b)) / (layout.trimW + 2 * b);
  const glyphDefs = new Map<string, string>();
  const fontIndex = new Map<string, number>();
  const gref = (fontId: string, gid: number) => {
    let fi = fontIndex.get(fontId);
    if (fi === undefined) fontIndex.set(fontId, (fi = fontIndex.size));
    const id = `${uid}g${fi}_${gid}`;
    if (!glyphDefs.has(id)) glyphDefs.set(id, browserFonts.glyphPath(fontId, gid));
    return id;
  };
  const clips: { id: string; r: Rect & { rr: number } }[] = [];
  const hairline = Math.max(0.15, (layout.trimW / widthPx) * 1);
  const body = layout.prims.map((p, i) => renderPrim(p, i));

  function renderPrim(p: Prim, i: number) {
    switch (p.k) {
      case 'rect':
        return <rect key={i} x={p.x} y={p.y} width={p.w} height={p.h} rx={p.r || undefined} fill={p.fill ? toCss(p.fill) : 'none'} fillOpacity={p.opacity < 1 ? p.opacity : undefined}
          stroke={p.stroke && p.stroke.widthPt > 0 ? toCss(p.stroke.color) : undefined} strokeWidth={p.stroke ? p.stroke.widthPt * MM_PER_PT : undefined} />;
      case 'line':
        return <line key={i} x1={p.x1} y1={p.y1} x2={p.x2} y2={p.y2} stroke={toCss(p.stroke.color)} strokeWidth={p.stroke.widthPt * MM_PER_PT} />;
      case 'text': {
        const f = browserFonts.get(p.fontId);
        if (!f) return null;
        const k = (p.sizePt * MM_PER_PT) / f.font.unitsPerEm;
        return (
          <g key={i} fill={toCss(p.color)} transform={`translate(${p.run.x} ${p.run.y})`}>
            {p.run.glyphs.map((g, j) => <use key={j} href={`#${gref(p.fontId, g.gid)}`} transform={`translate(${g.x} 0) scale(${k} ${-k})`} />)}
          </g>
        );
      }
      case 'image': {
        const cid = `${uid}c${i}`;
        clips.push({ id: cid, r: { ...p.clip, rr: p.r } });
        return (
          <image key={i} href={quality === 'thumb' ? thumbUrl(p.assetId) : previewUrl(p.assetId)} x={p.img.x} y={p.img.y} width={p.img.w} height={p.img.h}
            preserveAspectRatio="none" clipPath={`url(#${cid})`} />
        );
      }
      case 'placeholder': {
        const fs = Math.min(p.w / 14, p.h / 6);
        return (
          <g key={i}>
            <rect x={p.x} y={p.y} width={p.w} height={p.h} rx={p.r || undefined} fill={p.kind === 'logo' ? 'rgba(0,0,0,0.04)' : '#eceef1'} stroke="#a7b0bb" strokeWidth={hairline} strokeDasharray={`${hairline * 4} ${hairline * 3}`} />
            {quality !== 'thumb' && <text x={p.x + p.w / 2} y={p.y + p.h / 2 - (p.hint ? Math.max(2, Math.min(fs, 8)) * 0.55 : 0)} fontSize={Math.max(2, Math.min(fs, 8))} textAnchor="middle" dominantBaseline="middle" fill="#7b8794" fontFamily="system-ui, sans-serif">{p.kind === 'logo' ? 'Logo' : `Drop a photo · ${p.label}`}</text>}
            {quality !== 'thumb' && p.hint && <text x={p.x + p.w / 2} y={p.y + p.h / 2 + Math.max(2, Math.min(fs, 8)) * 0.6} fontSize={Math.max(1.6, Math.min(fs * 0.7, 5, p.w / (p.hint.length * 0.55)))} textAnchor="middle" dominantBaseline="middle" fill="#8a94a0" fontFamily="system-ui, sans-serif" data-hint>{p.hint}</text>}
          </g>
        );
      }
    }
  }

  return (
    <svg className="page-svg" role="img" aria-label={ariaLabel} viewBox={vb} width={widthPx} height={heightPx} style={{ width: widthPx, height: heightPx }}
      onClick={(e) => { if (e.target === e.currentTarget) onSelect?.(null, e); }}>
      <defs>
        {[...glyphDefs].map(([id, d]) => <path key={id} id={id} d={d} />)}
        {clips.map((c) => <clipPath key={c.id} id={c.id}><rect x={c.r.x} y={c.r.y} width={c.r.w} height={c.r.h} rx={c.r.rr || undefined} /></clipPath>)}
      </defs>
      <rect x={-b} y={-b} width={layout.trimW + 2 * b} height={layout.trimH + 2 * b} fill="#fff" onClick={(e) => onSelect?.(null, e)} />
      {body}
      {showBleed && b > 0 && (
        <path d={`M${-b} ${-b}H${layout.trimW + b}V${layout.trimH + b}H${-b}Z M0 0V${layout.trimH}H${layout.trimW}V0Z`} fill="rgba(240,242,245,0.72)" fillRule="evenodd" pointerEvents="none" />
      )}
      {gridCells && gridCells.length > 0 && (
        <g pointerEvents="none" aria-hidden data-layout-grid>
          {gridCells.map((c, i) => <rect key={i} {...rectAttrs(c)} fill="rgba(16,185,129,0.06)" stroke="rgba(16,185,129,0.55)" strokeWidth={hairline} strokeDasharray={`${hairline * 2} ${hairline * 2}`} />)}
        </g>
      )}
      {guides && (
        <g pointerEvents="none" fill="none" strokeWidth={hairline}>
          {guides.bleed && b > 0 && <rect x={0} y={0} width={layout.trimW} height={layout.trimH} stroke="#e11d48" strokeDasharray={`${hairline * 6} ${hairline * 3}`} />}
          {guides.safe && <rect {...rectAttrs(geometry.safe)} stroke="#2563eb" strokeDasharray={`${hairline * 3} ${hairline * 3}`} />}
          {guides.binding && geometry.bindings.map((b, i) => (
            <g key={i}>
              <rect {...rectAttrs(b)} fill="rgba(234,88,12,0.10)" stroke="#ea580c" strokeDasharray={`${hairline * 2} ${hairline * 2}`} />
              <text x={b.x + 2} y={b.y + Math.min(5, b.h - 1)} fontSize={Math.min(3, b.h * 0.6)} fill="#c2410c" fontFamily="system-ui, sans-serif">{b.label ?? 'Binding / hanging clearance'}</text>
            </g>
          ))}
        </g>
      )}
      {onSelect && (
        <g>
          {layout.boxes.map((box) => (
            <rect key={box.id} {...rectAttrs(box.rect)} fill="transparent" stroke={selectedId === box.id ? '#2f6fdb' : 'transparent'} strokeWidth={hairline * 2}
              style={{ cursor: selectedId === box.id ? 'move' : 'pointer' }}
              data-el={box.id} aria-label={box.name}
              onPointerDown={(e) => { if (selectedId === box.id) onBoxPointerDown?.(box, e); }}
              onClick={(e) => { e.stopPropagation(); onSelect(box.id, e); }}
              onMouseEnter={(e) => { if (selectedId !== box.id) (e.currentTarget as SVGRectElement).setAttribute('stroke', 'rgba(47,111,219,0.45)'); }}
              onMouseLeave={(e) => { if (selectedId !== box.id) (e.currentTarget as SVGRectElement).setAttribute('stroke', 'transparent'); }} />
          ))}
          {onHandlePointerDown && layout.boxes.filter((bx) => bx.id === selectedId).map((bx) => {
            const s = hairline * 7, r = bx.rect;
            const pos: Record<Handle, [number, number]> = { nw: [r.x, r.y], n: [r.x + r.w / 2, r.y], ne: [r.x + r.w, r.y], e: [r.x + r.w, r.y + r.h / 2], se: [r.x + r.w, r.y + r.h], s: [r.x + r.w / 2, r.y + r.h], sw: [r.x, r.y + r.h], w: [r.x, r.y + r.h / 2] };
            return HANDLES.map((h) => (
              <rect key={`h-${h}`} x={pos[h][0] - s / 2} y={pos[h][1] - s / 2} width={s} height={s} fill="#fff" stroke="#2f6fdb" strokeWidth={hairline * 1.5}
                style={{ cursor: `${h}-resize` }} data-handle={h} aria-label={`Resize ${h}`}
                onPointerDown={(e) => { e.stopPropagation(); onHandlePointerDown(bx, h, e); }} />
            ));
          })}
          {overridden && layout.boxes.filter((bx) => overridden.has(bx.id)).map((bx) => (
            <circle key={`o-${bx.id}`} cx={bx.rect.x + bx.rect.w} cy={bx.rect.y} r={hairline * 4} fill="#8b5cf6" stroke="#fff" strokeWidth={hairline} pointerEvents="none" />
          ))}
        </g>
      )}
    </svg>
  );
}

const rectAttrs = (r: Rect) => ({ x: r.x, y: r.y, width: r.w, height: r.h });

export const PageSvg = memo(PageSvgImpl);
